import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus, ChevronRight, ChevronDown, FileText, Trash2, FolderOpen, Pencil, FileUp, X, Upload, Eye, Download, HelpCircle, AlertTriangle, RefreshCw, FileSignature } from 'lucide-react';
import { Button, Modal, DateInput, EmptyState } from '../../common';
import { SearchableSelect } from '../../common/SearchableSelect';
import ConfirmDelete from '../common/ConfirmDelete';
import FileDropArea from '../common/FileDropArea';
import StatusDot from '../common/StatusDot';
import UserSelect from '../common/UserSelect';
import AddActivityForDocumentButton from '../common/AddActivityForDocumentButton';
import { getDeliverables, createDeliverable, updateDeliverable, deleteDeliverable, deleteDeliverableDocument, getContracts, addDeliverableDocument, updateDeliverableDocument } from '../../../services/dossier.service';
import { downloadAttachmentFile } from '../../../services/attachments.service';
import { getDeliverableDocTypes, type DeliverableDocType, getDeliverableTemplateSuggestions, type DeliverableTemplateSuggestion } from '../../../services/nomenclatoare.service';
import type { Deliverable, DossierContract } from '../../../types';

interface Props {
  dossierId: number;
  projectId: number;
  summary: { total: number; approved: number; completed: number; inProgress: number; pending: number };
  statusColor: 'green' | 'yellow' | 'gray' | 'red';
  onRefresh: () => void;
}

const DELIVERABLE_TYPES = ['SF', 'PT', 'DALI', 'DTAC'];
const TYPE_LABELS: Record<string, string> = {
  SF: 'Studiu de Fezabilitate',
  PT: 'Proiect Tehnic',
  DALI: 'DALI (Documentatie Avizare Lucrari Interventii)',
  DTAC: 'DTAC (Documentatie Tehnica Autorizare Constructie)'
};

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Nefinalizat', color: 'gray' as const },
  { value: 'in_progress', label: 'In lucru', color: 'yellow' as const },
  { value: 'completed', label: 'Finalizat', color: 'blue' as const },
  { value: 'approved', label: 'Aprobat', color: 'green' as const },
];

const STATUS_LABELS: Record<string, string> = {
  pending: 'Nefinalizat', in_progress: 'In lucru', completed: 'Finalizat', approved: 'Aprobat',
};

const STATUS_BADGE_CLASSES: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-600', in_progress: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-blue-100 text-blue-700', approved: 'bg-green-100 text-green-700',
};

const STATUS_DOT_CLASSES: Record<string, string> = {
  pending: 'bg-gray-300', in_progress: 'bg-yellow-500',
  completed: 'bg-blue-500', approved: 'bg-green-500',
};

export default function LivrabileSection({ dossierId, projectId, summary, statusColor, onRefresh }: Props) {
  const navigate = useNavigate();
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [contracts, setContracts] = useState<DossierContract[]>([]);
  const [statusDropdownId, setStatusDropdownId] = useState<number | null>(null);
  const [docTypes, setDocTypes] = useState<DeliverableDocType[]>([]);
  const [templateSuggestions, setTemplateSuggestions] = useState<Record<string, DeliverableTemplateSuggestion[]>>({});

  // Add type modal
  const [showAddTypeModal, setShowAddTypeModal] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [addTypeName, setAddTypeName] = useState('');
  const [addTypeNotes, setAddTypeNotes] = useState('');
  const [selectedContractId, setSelectedContractId] = useState('');
  const [addTypeResponsible, setAddTypeResponsible] = useState<number | null>(null);
  const [creatingType, setCreatingType] = useState(false);

  // Edit node modal
  const [editingNodeId, setEditingNodeId] = useState<number | null>(null);
  const [editNodeForm, setEditNodeForm] = useState({ name: '', code: '', notes: '', status: 'pending', due_date: '', contract_id: '', responsible_user_id: null as number | null });

  // Add sub-node modal
  const [addSubNodeParentId, setAddSubNodeParentId] = useState<number | null>(null);
  const [addSubNodeName, setAddSubNodeName] = useState('');
  const [addSubNodeCode, setAddSubNodeCode] = useState('');
  const [addSubNodeNotes, setAddSubNodeNotes] = useState('');
  const [creatingSubNode, setCreatingSubNode] = useState(false);

  // Add document modal
  const [addDocNodeId, setAddDocNodeId] = useState<number | null>(null);
  const [addDocForm, setAddDocForm] = useState({ title: '', notes: '', document_type_id: '', nr_registratura: '', data_registratura: '', data_document: '', data_expirare: '' });
  const [addDocFiles, setAddDocFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  // Edit document modal
  const [editDocId, setEditDocId] = useState<number | null>(null);
  const [editDocData, setEditDocData] = useState({ title: '', notes: '', document_type_id: '', nr_registratura: '', data_registratura: '', data_document: '', data_expirare: '' });
  const [editDocInitialTypeId, setEditDocInitialTypeId] = useState('');
  const [editDocFiles, setEditDocFiles] = useState<File[]>([]);
  const [editDocNodeId, setEditDocNodeId] = useState<number | null>(null);
  const [editDocGroupIds, setEditDocGroupIds] = useState<number[]>([]);
  const [savingDoc, setSavingDoc] = useState(false);

  // Delete confirmation
  const [pendingDelete, setPendingDelete] = useState<{ action: () => void; message: string } | null>(null);

  const loadDeliverables = useCallback(async () => {
    try {
      const data = await getDeliverables(dossierId);
      setDeliverables(data);
      setLoadError(false);
      setExpanded(prev => {
        if (prev.size > 0) return prev;
        return new Set(data.filter(d => d.depth === 0).map(d => d.id));
      });
    } catch (err) {
      console.error(err);
      setLoadError(true);
    }
    finally { setInitialLoading(false); }
  }, [dossierId]);

  const retryLoad = () => { setInitialLoading(true); setLoadError(false); loadDeliverables(); };

  const goToContracte = () => navigate(`/projects/${projectId}#contracte`, { replace: true });

  useEffect(() => { loadDeliverables(); }, [loadDeliverables]);
  useEffect(() => {
    getContracts(dossierId).then(setContracts).catch(console.error);
    getDeliverableDocTypes().then(types => setDocTypes(types.filter(t => t.is_active))).catch(console.error);
  }, [dossierId]);

  const loadTemplateSuggestions = async (type: string) => {
    if (templateSuggestions[type]) return;
    try {
      const suggestions = await getDeliverableTemplateSuggestions(type);
      setTemplateSuggestions(prev => ({ ...prev, [type]: suggestions }));
    } catch (err) { console.error(err); }
  };

  // --- Handlers ---
  // Contractul este obligatoriu la adăugare atâta timp cât dosarul are cel puțin un contract.
  const contractRequired = contracts.length > 0;

  const handleAddType = async () => {
    if (!selectedType) return;
    if (contractRequired && !selectedContractId) return;
    setCreatingType(true);
    try {
      const payload: any = { deliverable_type: selectedType };
      if (selectedContractId) payload.contract_id = parseInt(selectedContractId);
      if (addTypeName.trim()) payload.name = addTypeName.trim();
      if (addTypeNotes.trim()) payload.notes = addTypeNotes.trim();
      if (addTypeResponsible) payload.responsible_user_id = addTypeResponsible;
      await createDeliverable(dossierId, payload);
      setShowAddTypeModal(false);
      setSelectedContractId(''); setSelectedType(''); setAddTypeName(''); setAddTypeNotes(''); setAddTypeResponsible(null);
      await loadDeliverables(); onRefresh();
      toast.success('Livrabilul a fost adăugat.');
    } catch (err) {
      console.error(err);
      toast.error('Livrabilul nu a putut fi adăugat. Încearcă din nou.');
    }
    finally { setCreatingType(false); }
  };

  const handleAddSubNode = async () => {
    if (!addSubNodeParentId || !addSubNodeName.trim()) return;
    setCreatingSubNode(true);
    try {
      const parentNode = deliverables.find(d => d.id === addSubNodeParentId);
      await createDeliverable(dossierId, {
        parent_id: addSubNodeParentId,
        name: addSubNodeName.trim(),
        code: addSubNodeCode.trim() || undefined,
        notes: addSubNodeNotes.trim() || undefined,
        deliverable_type: parentNode?.deliverable_type || undefined,
      });
      setAddSubNodeParentId(null);
      setAddSubNodeName(''); setAddSubNodeCode(''); setAddSubNodeNotes('');
      await loadDeliverables(); onRefresh();
      toast.success('Elementul a fost adăugat.');
    } catch (err) {
      console.error(err);
      toast.error('Elementul nu a putut fi adăugat. Încearcă din nou.');
    }
    finally { setCreatingSubNode(false); }
  };

  const startEditingNode = (node: Deliverable) => {
    setEditingNodeId(node.id);
    setEditNodeForm({
      name: node.name, code: node.code || '', notes: node.notes || '',
      status: node.status || 'pending', due_date: node.due_date || '',
      contract_id: node.contract_id ? String(node.contract_id) : '',
      responsible_user_id: (node as any).responsible_user_id || null,
    });
  };

  const saveEditingNode = async () => {
    if (editingNodeId === null) return;
    const node = deliverables.find(d => d.id === editingNodeId);
    if (node) {
      const updates: any = {};
      if (editNodeForm.name.trim() !== node.name) updates.name = editNodeForm.name.trim();
      if (editNodeForm.code.trim() !== (node.code || '')) updates.code = editNodeForm.code.trim();
      if (editNodeForm.notes !== (node.notes || '')) updates.notes = editNodeForm.notes;
      if (editNodeForm.status !== (node.status || 'pending')) updates.status = editNodeForm.status;
      if (editNodeForm.due_date !== (node.due_date || '')) updates.due_date = editNodeForm.due_date || null;
      const nodeContractId = node.contract_id ? String(node.contract_id) : '';
      if (editNodeForm.contract_id !== nodeContractId) updates.contract_id = editNodeForm.contract_id ? parseInt(editNodeForm.contract_id) : null;
      if (editNodeForm.responsible_user_id !== ((node as any).responsible_user_id || null)) updates.responsible_user_id = editNodeForm.responsible_user_id;
      if (Object.keys(updates).length > 0) {
        try {
          await updateDeliverable(dossierId, editingNodeId, updates);
          await loadDeliverables(); onRefresh();
          toast.success('Modificările au fost salvate.');
        } catch (err) {
          console.error(err);
          toast.error('Modificările nu au putut fi salvate. Încearcă din nou.');
          return;
        }
      }
    }
    setEditingNodeId(null);
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    setStatusDropdownId(null);
    try {
      await updateDeliverable(dossierId, id, { status: newStatus });
      await loadDeliverables(); onRefresh();
      toast.success('Statusul a fost actualizat.');
    } catch (err) {
      console.error(err);
      toast.error('Statusul nu a putut fi actualizat. Încearcă din nou.');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteDeliverable(dossierId, id);
      await loadDeliverables(); onRefresh();
      toast.success('Livrabilul a fost șters.');
    } catch (err) {
      console.error(err);
      toast.error('Livrabilul nu a putut fi șters. Încearcă din nou.');
    }
  };

  const handleDeleteDocument = async (deliverableId: number, docId: number) => {
    try {
      await deleteDeliverableDocument(deliverableId, docId);
      await loadDeliverables(); onRefresh();
      toast.success('Fișierul a fost șters.');
    } catch (err) {
      console.error(err);
      toast.error('Fișierul nu a putut fi șters. Încearcă din nou.');
    }
  };

  const handleAddDocuments = async () => {
    if (!addDocNodeId || addDocFiles.length === 0) return;
    setUploading(true);
    try {
      for (const file of addDocFiles) {
        await addDeliverableDocument(addDocNodeId, file, {
          title: addDocForm.title || undefined,
          notes: addDocForm.notes || undefined,
          document_type_id: addDocForm.document_type_id ? parseInt(addDocForm.document_type_id) : undefined,
          nr_registratura: addDocForm.nr_registratura || undefined,
          data_registratura: addDocForm.data_registratura || undefined,
          data_document: addDocForm.data_document || undefined,
          data_expirare: addDocForm.data_expirare || undefined,
        });
      }
      setAddDocNodeId(null);
      setAddDocForm({ title: '', notes: '', document_type_id: '', nr_registratura: '', data_registratura: '', data_document: '', data_expirare: '' });
      setAddDocFiles([]);
      await loadDeliverables(); onRefresh();
      toast.success(addDocFiles.length === 1 ? 'Documentul a fost încărcat.' : 'Documentele au fost încărcate.');
    } catch (err) {
      console.error(err);
      toast.error('Documentul nu a putut fi încărcat. Încearcă din nou.');
    }
    finally { setUploading(false); }
  };

  const startEditDoc = (doc: any, nodeId: number, allDocs: any[]) => {
    setEditDocId(doc.id);
    const currentTypeId = doc.document_type_id ? String(doc.document_type_id) : '';
    setEditDocData({ title: doc.title || '', notes: doc.notes || '', document_type_id: currentTypeId, nr_registratura: doc.nr_registratura || '', data_registratura: toDateStr(doc.data_registratura), data_document: toDateStr(doc.data_document), data_expirare: toDateStr(doc.data_expirare) });
    setEditDocInitialTypeId(currentTypeId);
    setEditDocFiles([]);
    setEditDocNodeId(nodeId);
    const ids = allDocs.filter((d: any) => (d.title || '') === (doc.title || '') && (d.notes || '') === (doc.notes || '')).map((d: any) => d.id);
    setEditDocGroupIds(ids.length > 1 ? ids : [doc.id]);
  };

  const handleSaveDoc = async () => {
    if (!editDocId || !editDocNodeId) return;
    setSavingDoc(true);
    try {
      // Tipul se trimite doar dacă a fost schimbat, ca să nu suprascrie tipul existent.
      const typeChanged = editDocData.document_type_id !== editDocInitialTypeId && editDocData.document_type_id !== '';
      for (const id of editDocGroupIds) {
        await updateDeliverableDocument(editDocNodeId, id, {
          title: editDocData.title,
          notes: editDocData.notes,
          document_type_id: typeChanged ? parseInt(editDocData.document_type_id) : undefined,
          nr_registratura: editDocData.nr_registratura || undefined,
          data_registratura: editDocData.data_registratura || undefined,
          data_document: editDocData.data_document || undefined,
          data_expirare: editDocData.data_expirare || undefined,
        });
      }
      if (editDocFiles.length > 0) {
        for (const file of editDocFiles) {
          await addDeliverableDocument(editDocNodeId, file, {
            title: editDocData.title || undefined,
            notes: editDocData.notes || undefined,
            document_type_id: editDocData.document_type_id ? parseInt(editDocData.document_type_id) : undefined,
          });
        }
      }
      setEditDocId(null); setEditDocGroupIds([]); setEditDocFiles([]); setEditDocNodeId(null); setEditDocInitialTypeId('');
      await loadDeliverables(); onRefresh();
      toast.success('Documentul a fost salvat.');
    } catch (err) {
      console.error(err);
      toast.error('Documentul nu a putut fi salvat. Încearcă din nou.');
    }
    finally { setSavingDoc(false); }
  };

  // Deschide modalul „Adaugă document" pre-țintit pe nodul dat (refolosește fluxul existent addDeliverableDocument).
  const openAddDoc = (nodeId: number) => {
    setAddDocNodeId(nodeId);
    setAddDocForm({ title: '', notes: '', document_type_id: '', nr_registratura: '', data_registratura: '', data_document: '', data_expirare: '' });
    setAddDocFiles([]);
  };

  const toggleExpand = (id: number) => {
    setExpanded(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  const rootNodes = deliverables.filter(d => d.parent_id === null);
  const childrenOf = (parentId: number) => deliverables.filter(d => d.parent_id === parentId);
  const contractMap = new Map(contracts.map(c => [c.id, c.contract_name]));
  const badge = summary.total > 0
    ? `${summary.approved}/${summary.total} aprobate${summary.completed > 0 ? ` · ${summary.completed} finalizate` : ''}${summary.inProgress > 0 ? ` · ${summary.inProgress} în lucru` : ''}`
    : '0 livrabile';

  const getDocTypesForNode = (node: Deliverable): DeliverableDocType[] => {
    let root = node;
    while (root.parent_id !== null) { const parent = deliverables.find(d => d.id === root.parent_id); if (!parent) break; root = parent; }
    return docTypes.filter(dt => !dt.deliverable_type || dt.deliverable_type === root.deliverable_type);
  };

  const toDateStr = (v: any) => v ? String(v).substring(0, 10) : '';

  const inputClass = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500";

  // --- Render documents for a node ---
  const renderDocuments = (node: Deliverable) => {
    const docs = node.documents || [];
    const availableDocTypes = getDocTypesForNode(node);
    const groups: { key: string; title: string; notes: string; docTypeName: string; nr_registratura: string; data_registratura: string; data_document: string; data_expirare: string; docs: any[] }[] = [];
    for (const doc of docs) {
      const gKey = [doc.title || '', doc.notes || '', doc.nr_registratura || '', toDateStr(doc.data_registratura), toDateStr(doc.data_document), toDateStr(doc.data_expirare)].join('|||');
      const existing = groups.find(g => g.key === gKey);
      if (existing) { existing.docs.push(doc); }
      else { groups.push({ key: gKey, title: doc.title || '', notes: doc.notes || '', docTypeName: doc.document_type_name || '', nr_registratura: doc.nr_registratura || '', data_registratura: toDateStr(doc.data_registratura), data_document: toDateStr(doc.data_document), data_expirare: toDateStr(doc.data_expirare), docs: [doc] }); }
    }
    const fmtDate = (s: string) => s ? new Date(s).toLocaleDateString('ro-RO') : '';

    return (
      <div style={{ paddingLeft: `${(node.depth || 0) * 20 + 32}px`, paddingRight: '8px' }} className="pb-2">
        {docs.length === 0 ? (
          <div className="flex items-center gap-2 py-1">
            <span className="text-xs text-gray-400 italic">Niciun document încărcat</span>
            <button onClick={() => openAddDoc(node.id)}
              className="inline-flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-700 hover:bg-green-50 rounded px-1.5 py-0.5 transition-colors" title="Încarcă fișier">
              <Upload className="w-3.5 h-3.5" />
              Încarcă fișier
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-gray-400">({docs.length} {docs.length === 1 ? 'fișier' : 'fișiere'})</span>
              <button onClick={() => openAddDoc(node.id)}
                className="inline-flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-700 hover:bg-green-50 rounded px-1.5 py-0.5 transition-colors" title="Adaugă alt fișier">
                <Upload className="w-3.5 h-3.5" />
                Adaugă fișier
              </button>
            </div>
            <div className="space-y-1">
              {groups.map((group) => {
                const hasMeta = group.nr_registratura || group.data_registratura || group.data_document || group.data_expirare;
                const hasHeader = group.title || group.notes || group.docTypeName || hasMeta;
                return (
                <div key={group.key} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm px-4 py-3">
                  {hasHeader && (
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="flex-1 min-w-0">
                        {group.docTypeName && !group.title && <div className="text-sm text-gray-700 font-medium">{group.docTypeName}</div>}
                        {group.title && <div className="text-sm text-gray-700 font-medium">{group.title}</div>}
                        {group.notes && <div className="text-xs text-gray-500">{group.notes}</div>}
                        {hasMeta && (
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {group.nr_registratura && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-100" title="Nr. registratură">
                                Nr. {group.nr_registratura}{group.data_registratura ? ` / ${fmtDate(group.data_registratura)}` : ''}
                              </span>
                            )}
                            {!group.nr_registratura && group.data_registratura && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-100" title="Data registratură">
                                Reg. {fmtDate(group.data_registratura)}
                              </span>
                            )}
                            {group.data_document && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 border border-gray-200" title="Data document">
                                Doc. {fmtDate(group.data_document)}
                              </span>
                            )}
                            {group.data_expirare && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-700 border border-amber-100" title="Data expirare">
                                Exp. {fmtDate(group.data_expirare)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <button onClick={() => startEditDoc(group.docs[0], node.id, docs)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-100 transition-colors" title="Editează">
                        <Pencil className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    {group.docs.map((doc: any) => (
                      <div key={doc.id} className="flex items-center gap-2 text-sm bg-gray-50 rounded-lg px-3 py-2 hover:bg-blue-50 transition-colors">
                        <FileUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="flex-1 truncate text-gray-700 font-medium">{doc.file_name}</span>
                        <span className="text-gray-400 flex-shrink-0">{doc.file_size ? `${(doc.file_size / 1024).toFixed(0)} KB` : ''}</span>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          {doc.attachment_id ? (
                            <AddActivityForDocumentButton
                              projectId={projectId}
                              attachmentId={doc.attachment_id}
                              fileName={doc.file_name}
                              docTitle={group.title || group.docTypeName || undefined}
                              variant="icon"
                              size="sm"
                            />
                          ) : null}
                          {doc.file_name?.toLowerCase().endsWith('.pdf') && (
                            <button onClick={() => window.open(`/auth/files/${doc.attachment_id}/${encodeURIComponent(doc.file_name)}?inline=1`, '_blank')}
                              className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-100 transition-colors" title="Vizualizează">
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                          <button onClick={() => downloadAttachmentFile(doc.attachment_id, doc.file_name)}
                            className="p-1.5 text-gray-400 hover:text-green-600 rounded hover:bg-green-100 transition-colors" title="Descarcă">
                            <Download className="w-4 h-4" />
                          </button>
                          <button onClick={() => setPendingDelete({ action: () => handleDeleteDocument(node.id, doc.id), message: `Ești sigur că vrei să ștergi fișierul "${doc.file_name}"?` })}
                            className="p-1.5 text-gray-400 hover:text-red-500 rounded hover:bg-red-100 transition-colors" title="Șterge fișier">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  };

  // --- Render tree node ---
  const renderNode = (node: Deliverable) => {
    const children = childrenOf(node.id);
    const isRoot = node.parent_id === null;
    const isExpandable = children.length > 0;
    const isLeaf = children.length === 0 && !isRoot;
    const isExpanded = expanded.has(node.id);
    const dotColor: 'green' | 'blue' | 'yellow' | 'gray' =
      node.status === 'approved' ? 'green' : node.status === 'completed' ? 'blue' : node.status === 'in_progress' ? 'yellow' : 'gray';
    const approvedChildren = children.filter(c => c.status === 'approved').length;
    const isStatusDropdownOpen = statusDropdownId === node.id;

    return (
      <div key={node.id}>
        <div className={`flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors cursor-pointer group ${isRoot ? 'font-medium' : ''}`}
          style={{ paddingLeft: `${(node.depth || 0) * 20 + 8}px` }}>
          {isExpandable ? (
            <button onClick={() => toggleExpand(node.id)} className="flex-shrink-0">
              {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
            </button>
          ) : <span className="w-4" />}
          {isRoot ? <FolderOpen className="w-4 h-4 text-blue-500 flex-shrink-0" /> : <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />}
          <StatusDot color={dotColor} size="sm" />
          <span className="text-xs text-gray-400 flex-shrink-0">{node.code}</span>
          <span className={`flex-1 text-sm ${node.status === 'approved' ? 'text-gray-400' : 'text-gray-700'}`}>{node.name}</span>
          {!isRoot && (
            <div className="relative flex-shrink-0">
              <button onClick={() => setStatusDropdownId(isStatusDropdownOpen ? null : node.id)}
                className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE_CLASSES[node.status] || STATUS_BADGE_CLASSES.pending}`}>
                {STATUS_LABELS[node.status] || 'Nefinalizat'}
              </button>
              {isStatusDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setStatusDropdownId(null)} />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white border rounded-lg shadow-lg py-1 min-w-[140px]">
                    {STATUS_OPTIONS.map(opt => (
                      <button key={opt.value} onClick={() => handleStatusChange(node.id, opt.value)}
                        className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2 ${node.status === opt.value ? 'font-medium bg-gray-50' : ''}`}>
                        <StatusDot color={opt.color} size="sm" />{opt.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {isRoot && (node.contract_id ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-100 truncate max-w-[220px] flex-shrink-0"
              title={contractMap.get(node.contract_id) || 'Contract asociat'}>
              {contractMap.get(node.contract_id) || 'Contract asociat'}
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-red-50 text-red-700 border border-red-200 font-medium flex-shrink-0"
              title="Acest livrabil nu are un contract asociat. Editează-l pentru a-l corela cu un contract.">
              Fără contract
            </span>
          ))}
          {isRoot && children.length > 0 && <span className="text-xs text-gray-400">{approvedChildren}/{children.length}</span>}
          <div className="flex items-center gap-1 flex-shrink-0">
            {isRoot && (
              <button onClick={(e) => { e.stopPropagation(); setAddSubNodeParentId(node.id); setAddSubNodeName(''); setAddSubNodeCode(''); setAddSubNodeNotes(''); if (node.deliverable_type) loadTemplateSuggestions(node.deliverable_type); }}
                className="p-1.5 rounded hover:bg-primary-100 text-gray-400 hover:text-primary-600 transition-colors" title="Adaugă element">
                <Plus className="w-4 h-4" />
              </button>
            )}
            <button onClick={(e) => { e.stopPropagation(); openAddDoc(node.id); }}
              className="p-1.5 rounded hover:bg-green-100 text-gray-400 hover:text-green-600 transition-colors" title="Adaugă document">
              <Upload className="w-4 h-4" />
            </button>
            <button onClick={() => startEditingNode(node)}
              className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-100 transition-colors" title="Editează">
              <Pencil className="w-4 h-4" />
            </button>
            {isRoot && (
              <button onClick={() => setPendingDelete({ action: () => handleDelete(node.id), message: `Ești sigur că vrei să ștergi "${node.name}"? Toate sub-elementele și documentele asociate vor fi șterse.` })} className="p-1.5 text-gray-400 hover:text-red-500 rounded hover:bg-red-100 transition-colors" title="Șterge">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        {node.notes && editingNodeId !== node.id && (
          <div style={{ paddingLeft: `${(node.depth || 0) * 20 + 32}px`, paddingRight: '8px' }} className="pb-1">
            <div className="text-xs text-gray-500 italic">{node.notes}</div>
          </div>
        )}
        {(isLeaf || isRoot) && renderDocuments(node)}
        {isExpanded && children.map(child => renderNode(child))}
      </div>
    );
  };

  // Helper: get the parent node for addSubNodeParentId
  const addSubNodeParent = addSubNodeParentId ? deliverables.find(d => d.id === addSubNodeParentId) : null;
  // Helper: get the node for addDocNodeId
  const addDocNode = addDocNodeId ? deliverables.find(d => d.id === addDocNodeId) : null;
  const addDocAvailableTypes = addDocNode ? getDocTypesForNode(addDocNode) : [];
  // Helper: tipurile de document disponibile pentru documentul aflat în editare
  const editDocNode = editDocNodeId ? deliverables.find(d => d.id === editDocNodeId) : null;
  const editDocAvailableTypes = (() => {
    if (!editDocNode) return [];
    const types = getDocTypesForNode(editDocNode);
    // Păstrăm tipul curent în listă chiar dacă a fost dezactivat sau e al altui tip de livrabil
    if (editDocInitialTypeId && !types.some(t => String(t.id) === editDocInitialTypeId)) {
      const current = docTypes.find(t => String(t.id) === editDocInitialTypeId);
      if (current) return [current, ...types];
    }
    return types;
  })();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-600 font-medium">{badge}</div>
        {contracts.length === 0 ? (
          <Button variant="secondary" size="sm" leftIcon={<FileSignature className="w-4 h-4" />} onClick={goToContracte}>Creează întâi un contract</Button>
        ) : (
          <Button variant="primary" size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => setShowAddTypeModal(true)}>Adaugă livrabil</Button>
        )}
      </div>

      {/* Add Type Modal */}
      <Modal isOpen={showAddTypeModal} onClose={() => setShowAddTypeModal(false)} title="Adaugă livrabil" size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => setShowAddTypeModal(false)}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleAddType} disabled={!selectedType || (contractRequired && !selectedContractId) || creatingType} isLoading={creatingType}>
              {creatingType ? 'Se creează...' : 'Creează'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Contractul asociat e primul câmp: livrabilul aparține unui contract, deci alegi întâi „de care contract ține". */}
          {contracts.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contract asociat *</label>
              <SearchableSelect
                options={contracts.map(c => ({ value: c.id, label: c.contract_name }))}
                value={selectedContractId}
                onChange={v => setSelectedContractId(String(v))}
                placeholder="-- Selectează contractul --"
                error={!selectedContractId ? 'Selectarea unui contract este obligatorie.' : undefined}
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tip livrabil *</label>
            <SearchableSelect
              options={DELIVERABLE_TYPES.map(type => ({ value: type, label: `${type} — ${TYPE_LABELS[type]}` }))}
              value={selectedType}
              onChange={v => setSelectedType(v as string)}
              placeholder="-- Selectează tipul --"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titlu</label>
            <input value={addTypeName} onChange={e => setAddTypeName(e.target.value)} placeholder="Titlu personalizat (opțional)" className={inputClass} />
          </div>
          <UserSelect projectId={projectId} value={addTypeResponsible} onChange={setAddTypeResponsible} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observații</label>
            <textarea value={addTypeNotes} onChange={e => setAddTypeNotes(e.target.value)} rows={2} placeholder="Observații (opțional)" className={inputClass} />
          </div>
        </div>
      </Modal>

      {/* Edit Node Modal */}
      <Modal isOpen={editingNodeId !== null} onClose={() => setEditingNodeId(null)} title="Editare element" size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => setEditingNodeId(null)}>Anulează</Button>
            <Button variant="primary" size="md" onClick={saveEditingNode}>Salvează</Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            {editNodeForm.code && (
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Cod</label>
                <div className="px-3 py-2 text-sm text-gray-500 bg-gray-100 border border-gray-200 rounded-lg">{editNodeForm.code}</div>
              </div>
            )}
            <div className={editNodeForm.code ? "col-span-3" : "col-span-4"}>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titlu</label>
              <input value={editNodeForm.name} onChange={e => setEditNodeForm(f => ({...f, name: e.target.value}))} placeholder="Titlul" className={inputClass} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <SearchableSelect
                options={STATUS_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))}
                value={editNodeForm.status}
                onChange={v => setEditNodeForm(f => ({...f, status: v as string}))}
              />
            </div>
            <DateInput label="Dată limită" value={editNodeForm.due_date} onChange={e => setEditNodeForm(f => ({...f, due_date: e.target.value}))} />
          </div>
          {contracts.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contract asociat</label>
              <SearchableSelect
                options={contracts.map(c => ({ value: c.id, label: c.contract_name }))}
                value={editNodeForm.contract_id}
                onChange={v => setEditNodeForm(f => ({...f, contract_id: String(v)}))}
                placeholder="— Fără contract —"
              />
            </div>
          )}
          <UserSelect projectId={projectId} value={editNodeForm.responsible_user_id} onChange={userId => setEditNodeForm(f => ({...f, responsible_user_id: userId}))} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observații</label>
            <textarea value={editNodeForm.notes} onChange={e => setEditNodeForm(f => ({...f, notes: e.target.value}))} rows={2} placeholder="Observații" className={inputClass} />
          </div>
        </div>
      </Modal>

      {/* Add Sub-Node Modal */}
      <Modal isOpen={addSubNodeParentId !== null} onClose={() => setAddSubNodeParentId(null)} title="Adaugă element" size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => setAddSubNodeParentId(null)}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleAddSubNode} disabled={creatingSubNode || !addSubNodeName.trim()} isLoading={creatingSubNode}
              leftIcon={!creatingSubNode ? <Plus className="w-4 h-4" /> : undefined}>
              {creatingSubNode ? 'Se creează...' : 'Adaugă'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {addSubNodeParent?.deliverable_type && templateSuggestions[addSubNodeParent.deliverable_type]?.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Selectează din șablon</label>
              <SearchableSelect
                options={templateSuggestions[addSubNodeParent.deliverable_type].filter(s => !s.parentCode).map(s => ({ value: s.code, label: `${s.code} — ${s.name}` }))}
                value=""
                onChange={v => {
                  const suggestion = templateSuggestions[addSubNodeParent!.deliverable_type!].find(s => s.code === v);
                  if (suggestion) { setAddSubNodeName(suggestion.name); setAddSubNodeCode(suggestion.code); }
                }}
                placeholder="-- Alege din sugestii sau completează manual --"
              />
            </div>
          )}
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Cod</label>
              <input value={addSubNodeCode} onChange={e => setAddSubNodeCode(e.target.value)} placeholder="ex: 1.1.1" className={inputClass} />
            </div>
            <div className="col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Titlu *</label>
              <input value={addSubNodeName} onChange={e => setAddSubNodeName(e.target.value)} placeholder="ex: Parte scrisă" className={inputClass} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observații</label>
            <textarea value={addSubNodeNotes} onChange={e => setAddSubNodeNotes(e.target.value)} rows={2} placeholder="Observații (opțional)" className={inputClass} />
          </div>
        </div>
      </Modal>

      {/* Add Document Modal */}
      <Modal isOpen={addDocNodeId !== null} onClose={() => { setAddDocNodeId(null); setAddDocFiles([]); }} title="Adaugă document" size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => { setAddDocNodeId(null); setAddDocFiles([]); }}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleAddDocuments} disabled={uploading || addDocFiles.length === 0} isLoading={uploading}
              leftIcon={!uploading ? <Upload className="w-4 h-4" /> : undefined}>
              {uploading ? 'Se încarcă...' : `Încarcă${addDocFiles.length > 0 ? ` (${addDocFiles.length})` : ''}`}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {addDocAvailableTypes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tip document</label>
              <SearchableSelect
                options={addDocAvailableTypes.map(dt => ({ value: dt.id, label: `${dt.code ? `${dt.code} - ` : ''}${dt.name}` }))}
                value={addDocForm.document_type_id}
                onChange={v => {
                  const dtId = String(v);
                  const dt = addDocAvailableTypes.find(t => String(t.id) === dtId);
                  setAddDocForm(d => ({ ...d, document_type_id: dtId, title: dt ? dt.name : d.title }));
                }}
                placeholder="-- Selectează tipul --"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titlu</label>
            <input value={addDocForm.title} onChange={e => setAddDocForm(d => ({ ...d, title: e.target.value }))} placeholder="Titlul documentului (opțional)" className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observații</label>
            <textarea value={addDocForm.notes} onChange={e => setAddDocForm(d => ({ ...d, notes: e.target.value }))} rows={2} placeholder="Observații (opțional)" className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                <span>Nr. registratură</span>
                <span title="Numărul unic atribuit de registratura instituției la înregistrarea documentului" className="cursor-help text-gray-400 hover:text-gray-600">
                  <HelpCircle className="w-3.5 h-3.5" />
                </span>
              </label>
              <input value={addDocForm.nr_registratura} onChange={e => setAddDocForm(d => ({ ...d, nr_registratura: e.target.value }))} placeholder="Nr. registratură" className={inputClass} />
            </div>
            <DateInput label="Data registratură" tooltip="Data la care documentul a fost înregistrat la registratura instituției" value={addDocForm.data_registratura} onChange={e => setAddDocForm(d => ({ ...d, data_registratura: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DateInput label="Data document" tooltip="Data emiterii/semnării documentului" value={addDocForm.data_document} onChange={e => setAddDocForm(d => ({ ...d, data_document: e.target.value }))} />
            <DateInput label="Data expirare" tooltip="Data până la care documentul este valabil (avize, autorizații)" value={addDocForm.data_expirare} onChange={e => setAddDocForm(d => ({ ...d, data_expirare: e.target.value }))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fișiere *</label>
            <FileDropArea files={addDocFiles} onFilesChange={setAddDocFiles} id="add-del-doc-modal" />
          </div>
        </div>
      </Modal>

      {/* Edit Document Modal */}
      <Modal isOpen={editDocId !== null} onClose={() => { setEditDocId(null); setEditDocFiles([]); }} title="Editare document" size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => { setEditDocId(null); setEditDocFiles([]); }}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleSaveDoc} disabled={savingDoc} isLoading={savingDoc}>
              {savingDoc ? 'Se salvează...' : 'Salvează'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {editDocAvailableTypes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tip document</label>
              <SearchableSelect
                options={editDocAvailableTypes.map(dt => ({ value: dt.id, label: `${dt.code ? `${dt.code} - ` : ''}${dt.name}` }))}
                value={editDocData.document_type_id}
                onChange={v => setEditDocData(d => ({ ...d, document_type_id: String(v) }))}
                placeholder="-- Selectează tipul --"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titlu</label>
            <input value={editDocData.title} onChange={e => setEditDocData(d => ({ ...d, title: e.target.value }))} placeholder="Titlul documentului" className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observații</label>
            <textarea value={editDocData.notes} onChange={e => setEditDocData(d => ({ ...d, notes: e.target.value }))} rows={2} placeholder="Observații" className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                <span>Nr. registratură</span>
                <span title="Numărul unic atribuit de registratura instituției la înregistrarea documentului" className="cursor-help text-gray-400 hover:text-gray-600">
                  <HelpCircle className="w-3.5 h-3.5" />
                </span>
              </label>
              <input value={editDocData.nr_registratura} onChange={e => setEditDocData(d => ({ ...d, nr_registratura: e.target.value }))} placeholder="Nr. registratură" className={inputClass} />
            </div>
            <DateInput label="Data registratură" tooltip="Data la care documentul a fost înregistrat la registratura instituției" value={editDocData.data_registratura} onChange={e => setEditDocData(d => ({ ...d, data_registratura: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DateInput label="Data document" tooltip="Data emiterii/semnării documentului" value={editDocData.data_document} onChange={e => setEditDocData(d => ({ ...d, data_document: e.target.value }))} />
            <DateInput label="Data expirare" tooltip="Data până la care documentul este valabil (avize, autorizații)" value={editDocData.data_expirare} onChange={e => setEditDocData(d => ({ ...d, data_expirare: e.target.value }))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Adaugă fișiere noi</label>
            <FileDropArea files={editDocFiles} onFilesChange={setEditDocFiles} id="edit-del-doc-modal" compact />
          </div>
        </div>
      </Modal>

      {initialLoading ? (
        <div className="text-center py-8 text-sm text-gray-500">Se încarcă...</div>
      ) : loadError ? (
        <EmptyState
          icon={<AlertTriangle className="w-12 h-12 text-red-400" />}
          title="Livrabilele nu au putut fi încărcate"
          description="A apărut o eroare la încărcarea livrabilelor. Verifică conexiunea și încearcă din nou."
          action={{ label: 'Reîncearcă', onClick: retryLoad }}
        />
      ) : rootNodes.length === 0 ? (
        contracts.length === 0 ? (
          <EmptyState
            icon={<FileSignature className="w-12 h-12 text-gray-400" />}
            title="Nu există încă niciun contract"
            description="Livrabilele (SF, PT, DALI, DTAC) se leagă de un contract. Creează întâi un contract în dosar, apoi revino aici pentru a adăuga livrabile."
            action={{ label: 'Creează întâi un contract', onClick: goToContracte }}
          />
        ) : (
          <EmptyState
            icon="file"
            title="Niciun livrabil adăugat"
            description="Adaugă primul livrabil (SF, PT, DALI sau DTAC) și asociază-l cu un contract al dosarului."
            action={{ label: 'Adaugă livrabil', onClick: () => setShowAddTypeModal(true) }}
          />
        )
      ) : (
        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <div className="divide-y divide-gray-100">
            {rootNodes.map(node => renderNode(node))}
          </div>
        </div>
      )}

      <ConfirmDelete
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => { if (pendingDelete) pendingDelete.action(); }}
        message={pendingDelete?.message}
      />
    </div>
  );
}
